import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, ArrowUpRight, ArrowDownRight, Bell, Loader2, RefreshCw, CreditCard, Gift, Coins, Store } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { transactionsApi } from '../../services/api';
import { hasDifferentCurrency, convertToLocalCurrency } from '../../utils/currency';

export default function DashboardTab({ onLoadingChange }) {
  const navigate = useNavigate();
  const { setShowNotifications, unreadCount, profileForm } = useAppState();
  const { user, refreshUser, isLoggedIn } = useAuth();
  const { isDesktop } = useResponsive();

  // Get time-based greeting (matches HomeTab)
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  const userFirstName = user?.first_name || profileForm?.firstName || 'there';

  // State for fetched data
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [pointsEarnedToday, setPointsEarnedToday] = useState(0);
  const [pointsUsedToday, setPointsUsedToday] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Get user's rewards balance from profile
  const rewardsBalance = user?.available_bold_credits || user?.bold_credits || user?.boldCredits || 0;

  // Debug: Log user data
  useEffect(() => {
    console.log('Dashboard: User data:', user);
    console.log('Dashboard: Rewards balance:', rewardsBalance);
    console.log('Dashboard: Is logged in:', isLoggedIn);
  }, [user, rewardsBalance, isLoggedIn]);

  // Format balance for display
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(Math.floor(num));
  };

  // Convert points to USD (100 points = $0.10)
  const pointsToUSD = (points) => {
    const usd = points / 1000;
    return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(usd)}`;
  };

  // Get local currency if different from USD
  const showLocalCurrency = hasDifferentCurrency(profileForm.country);
  const usdAmount = rewardsBalance / 1000;
  const localCurrency = showLocalCurrency
    ? convertToLocalCurrency(usdAmount, profileForm.country)
    : null;

  // Calculate points used today from recent transactions
  // Only counts transactions where points were SPENT (not earned/rewards)
  const calculatePointsUsedToday = useCallback((transactions) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return transactions
      .filter(tx => {
        const txDate = new Date(tx.created_at || tx.createdAt || tx.date);
        txDate.setHours(0, 0, 0, 0);
        return txDate.getTime() === today.getTime();
      })
      .filter(tx => {
        // Filter for transactions where points were used (payment_method = "POINTS" or "Points")
        const paymentMethod = (tx.payment_method || '').toUpperCase();
        if (paymentMethod !== 'POINTS') return false;

        // Exclude reward/credit transactions - these are points EARNED, not used
        const description = (tx.description || '').toLowerCase();
        const category = (tx.category || '').toLowerCase();
        const isReward = description.includes('sign up') ||
                        description.includes('signup') ||
                        description.includes('referral') ||
                        description.includes('bonus') ||
                        description.includes('reward') ||
                        description.includes('cashback') ||
                        category.includes('reward') ||
                        category.includes('bonus');

        // Only include if NOT a reward (i.e., actual point spending)
        return !isReward;
      })
      .reduce((total, tx) => {
        // Use bold_credits for points spent
        const amount = Math.abs(tx.bold_credits || tx.amount || 0);
        return total + amount;
      }, 0);
  }, []);

  // Format date for display (e.g., "Apr 4, 2026")
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get payment method type for icon display
  const getPaymentMethodType = (tx) => {
    // Check if it's a reward type transaction FIRST
    // Rewards should always show green gift icon, even if payment_method is POINTS
    const description = (tx.description || '').toLowerCase();
    if (description.includes('sign up') || description.includes('signup')) return 'reward';
    if (description.includes('referral')) return 'reward';
    if (description.includes('cashback')) return 'reward';
    if (description.includes('bonus')) return 'reward';
    if (description.includes('reward')) return 'reward';

    // Then check payment method for non-reward transactions
    const paymentMethod = (tx.payment_method || '').toUpperCase();
    if (paymentMethod === 'POINTS') return 'points';
    if (paymentMethod === 'CARD') return 'card';
    // External merchant (payment_method: "External Merchant")
    if (paymentMethod.includes('EXTERNAL') || paymentMethod.includes('MERCHANT')) return 'merchant';
    return 'reward';
  };

  // Fetch dashboard data
  useEffect(() => {
    // Only fetch if user is logged in
    if (!isLoggedIn) {
      console.log('Dashboard: User not logged in, skipping fetch');
      setIsLoading(false);
      return;
    }

    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        console.log('Dashboard: Fetching data...');

        // Refresh user profile to get latest balance
        refreshUser().catch(err => console.warn('Dashboard: Failed to refresh user:', err));

        // Fetch recent transactions and points earned today in parallel
        const [transactions, earnedTodayResponse] = await Promise.all([
          transactionsApi.getRecent().catch(err => {
            console.error('Dashboard: Failed to fetch recent transactions:', err);
            return [];
          }),
          transactionsApi.getPointsEarnedToday().catch(err => {
            console.error('Dashboard: Failed to fetch points earned today:', err);
            return { totalPoints: 0 };
          }),
        ]);

        console.log('Dashboard: Transactions received:', transactions?.length || 0);
        console.log('Dashboard: Points earned today:', earnedTodayResponse);

        setRecentTransactions(transactions || []);

        // Extract points earned today from response (API returns totalPoints)
        const earned = earnedTodayResponse?.totalPoints ||
                      earnedTodayResponse?.points ||
                      earnedTodayResponse?.pointsEarnedToday ||
                      0;
        setPointsEarnedToday(earned);

        // Calculate points used today from transactions
        const usedToday = calculatePointsUsedToday(transactions || []);
        setPointsUsedToday(usedToday);
      } catch (err) {
        console.error('Dashboard: Error fetching data:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [isLoggedIn, refreshUser, calculatePointsUsedToday, refreshKey]);

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  // Manual refresh function
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Transform transactions for display
  const recentActivity = recentTransactions.slice(0, 10).map((tx, index) => {
    const paymentMethod = (tx.payment_method || '').toUpperCase();
    const isPointsPayment = paymentMethod === 'POINTS';
    const isCardPayment = paymentMethod === 'CARD';
    const pointsUsed = tx.bold_credits || 0;
    const amountPaid = tx.amount || 0;
    const cashbackEarned = tx.metadata?.cashback || 0;

    // Determine transaction type for styling
    const description = (tx.description || '').toLowerCase();
    const isReward = description.includes('sign up') ||
                     description.includes('signup') ||
                     description.includes('referral') ||
                     description.includes('bonus') ||
                     description.includes('reward');

    let type = 'transaction';
    // Check rewards FIRST - signup rewards should always show as positive green
    if (isReward) type = 'reward';
    else if (isPointsPayment) type = 'points_used';
    else if (isCardPayment) type = 'card_payment';

    // Get display name for transaction
    const category = (tx.category || '').toLowerCase();
    const isAirtime = category.includes('pin') || category.includes('top up') || category.includes('airtime');
    let displayDesc;
    if (isAirtime) {
      // For airtime: show "₦{amount} Airtime"
      displayDesc = `₦${formatNumber(tx.amount || 0)} Airtime`;
    } else {
      // For others: use product name, remove " purchase" suffix
      const productName = tx.metadata?.product || tx.description || tx.category || 'Transaction';
      displayDesc = productName.replace(/ purchase$/i, '');
    }

    return {
      id: tx.transaction_uuid || tx.id || index,
      desc: displayDesc,
      type,
      date: formatDate(tx.created_at),
      paymentMethodType: getPaymentMethodType(tx),
      // For points payments: show points used (negative)
      pointsUsed: isPointsPayment ? pointsUsed : 0,
      // For card payments: show amount paid and cashback earned
      amountPaid: isCardPayment ? amountPaid : 0,
      cashbackEarned: cashbackEarned,
      // For rewards: show points earned
      pointsEarned: isReward ? (tx.bold_credits || tx.amount || 0) : 0,
      // Store full transaction for receipt page
      rawTransaction: tx,
    };
  });

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Mobile Header - matches HomeTab */}
        {!isDesktop && (
          <header className="sticky top-0 bg-white z-10 px-4 pb-3 flex items-center justify-between border-b border-gray-100 pt-[calc(0.75rem+env(safe-area-inset-top))]">
            <div className="flex items-center gap-3">
              <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="h-9 w-9" />
              <h1 className="font-bold text-gray-900 text-lg">
                {getGreeting()}, {userFirstName}
              </h1>
            </div>
            <button
              onClick={() => setShowNotifications(true)}
              className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <Bell size={22} />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-green-500 rounded-full" />
              )}
            </button>
          </header>
        )}
        <div className={`px-4 py-6 ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>
        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            Error loading data: {error}
          </div>
        )}

        {/* Main Balance Card */}
        <div className="bg-gradient-card rounded-2xl p-6 text-white mb-6 relative overflow-hidden">
          {/* Background coin icons */}
          <Coins size={80} className="absolute -right-4 -top-4 text-white/10 rotate-12" />
          <Coins size={60} className="absolute right-16 bottom-2 text-white/10 -rotate-12" />
          <Coins size={40} className="absolute left-1/2 -top-2 text-white/10 rotate-45" />

          <p className="text-sm text-white/70 font-medium mb-1 relative z-10">Your Reward Balance</p>
          <div className="flex items-baseline gap-1 mb-1 relative z-10">
            <span className="text-2xl font-bold">{formatNumber(rewardsBalance)}</span>
            <sup className="text-xs text-white/70 font-medium">PTS</sup>
          </div>
          <div className="flex items-center gap-2 relative z-10">
            <span className="text-base">{pointsToUSD(rewardsBalance)}</span>
            {localCurrency && (
              <>
                <span className="text-white/40 mx-1">|</span>
                <span className="text-base">{localCurrency.formatted}</span>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <ArrowUpRight size={16} className="text-emerald-600" />
              </div>
              <p className="text-sm text-gray-500">Earned Today</p>
            </div>
            <p className="text-2xl font-bold text-emerald-600">
              +{formatNumber(pointsEarnedToday)}<sup className="text-xs font-medium" style={{ marginLeft: '-1px' }}>PTS</sup>
            </p>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <ArrowDownRight size={16} className="text-amber-600" />
              </div>
              <p className="text-sm text-gray-500">Used Today</p>
            </div>
            <p className="text-2xl font-bold text-amber-600">
              {formatNumber(pointsUsedToday)}<sup className="text-xs font-medium" style={{ marginLeft: '-1px' }}>PTS</sup>
            </p>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-bold text-gray-900">Recent Activity</h2>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full disabled:opacity-50"
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {recentActivity.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">No recent activity</p>
                <p className="text-sm text-gray-400 mt-1">
                  {!isLoggedIn ? 'Please log in to see your transactions' : 'Your transactions will appear here'}
                </p>
                <button
                  onClick={handleRefresh}
                  className="mt-3 px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg"
                >
                  Tap to refresh
                </button>
              </div>
            ) : (
              recentActivity.map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigate(`/transaction/${item.id}`, { state: { transaction: item.rawTransaction } })}
                  className="w-full flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
                >
                  {/* Icon based on payment method */}
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      item.paymentMethodType === 'points' ? 'bg-amber-100' :
                      item.paymentMethodType === 'card' ? 'bg-blue-100' :
                      item.paymentMethodType === 'merchant' ? 'bg-purple-100' :
                      'bg-emerald-100'
                    }`}
                  >
                    {item.paymentMethodType === 'points' ? (
                      <Coins size={18} className="text-amber-600" />
                    ) : item.paymentMethodType === 'card' ? (
                      <CreditCard size={18} className="text-blue-600" />
                    ) : item.paymentMethodType === 'merchant' ? (
                      <Store size={18} className="text-purple-600" />
                    ) : (
                      <Gift size={18} className="text-emerald-600" />
                    )}
                  </div>

                  {/* Description and metadata */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{item.desc}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{item.date}</p>
                  </div>

                  {/* Amount display */}
                  <div className="flex flex-col items-end flex-shrink-0">
                    {item.type === 'points_used' && (
                      <span className="font-bold text-amber-600">
                        -{formatNumber(item.pointsUsed)} Pts
                      </span>
                    )}
                    {item.type === 'card_payment' && (
                      <>
                        <span className="font-bold text-gray-900">
                          -₦{formatNumber(item.amountPaid)}
                        </span>
                        {item.cashbackEarned > 0 && (
                          <span className="text-xs text-emerald-600 font-medium">
                            +{formatNumber(item.cashbackEarned)} Pts
                          </span>
                        )}
                      </>
                    )}
                    {item.type === 'reward' && (
                      <>
                        <span className="font-bold text-emerald-600">
                          +{formatNumber(item.pointsEarned)} Pts
                        </span>
                        {item.pointsEarned > 0 && (
                          <span className="text-xs text-gray-400">
                            USD {(item.pointsEarned / 1000).toFixed(2)}
                          </span>
                        )}
                      </>
                    )}
                    {item.type === 'transaction' && (
                      <span className="font-bold text-gray-900">
                        {formatNumber(item.amountPaid || item.pointsUsed || 0)}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
